// Configuration that works in both development and production
// In development: uses process.env.REACT_APP_* variables
// In production: uses window.APP_CONFIG (set by template substitution)

const parseBoolean = (value, fallback = false) => {
  if (typeof value === 'boolean') return value;
  if (typeof value !== 'string') return fallback;

  const normalized = value.trim().toLowerCase();
  if (normalized === 'true') return true;
  if (normalized === 'false') return false;
  return fallback;
};

const sanitizeString = (value, fallback = '') => {
  if (typeof value !== 'string') return fallback;

  const trimmed = value.trim();
  if (!trimmed) return fallback;
  if (/^\{\{.+\}\}$/.test(trimmed)) return fallback;

  return trimmed;
};

const parseAdProvider = (value, fallback = 'H5') => {
  const normalized = sanitizeString(value, fallback).toUpperCase();
  if (normalized === 'GPT' || normalized === 'H5' || normalized === 'VIGNETTE' || normalized === 'DISABLED') {
    return normalized;
  }
  return fallback;
};

const FRONTEND_POSTHOG_DENYLIST = [
  'account_clicked',
  'streak_clicked',
  'gettoncini_clicked',
  'word_counter_clicked',
  'drawer_menu_opened',
  'game_click_*',
  'game_instructions_viewed',
  'game_end_section_viewed',
  'game_guess_*',
  'game_help_viewed',
  'game_level_completed',
  'ui_modal_*_opened',
  'ui_modal_*_closed',
  'drawer_menu_closed',
  'drawer_menu_*_clicked',
];

const getConfig = () => {
  // Development environment: use REACT_APP_* variables
  if (process.env.NODE_ENV === 'development') {
    return {
      API_BASE_URL: process.env.REACT_APP_API_BASE_URL,
      GA_MEASUREMENT_ID: process.env.REACT_APP_GA_MEASUREMENT_ID,
      POSTHOG_PROJECT_TOKEN: process.env.REACT_APP_POSTHOG_PROJECT_TOKEN,
      POSTHOG_HOST: process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com',
      POSTHOG_ENABLED: parseBoolean(process.env.REACT_APP_POSTHOG_ENABLED, false),
      POSTHOG_EVENT_DENYLIST: FRONTEND_POSTHOG_DENYLIST,
      GIT_SHA: process.env.REACT_APP_GIT_SHA,
      CLERK_PUBLISHABLE_KEY: process.env.REACT_APP_CLERK_PUBLISHABLE_KEY,
      ENVIRONMENT: process.env.REACT_APP_ENVIRONMENT || 'development',
      SHOW_ADS: parseBoolean(process.env.REACT_APP_SHOW_ADS, false),
      AD_PROVIDER: parseAdProvider(process.env.REACT_APP_AD_PROVIDER, 'H5'),
      GPT_NETWORK_CODE: sanitizeString(process.env.REACT_APP_GPT_NETWORK_CODE),
      GPT_INTERSTITIAL_AD_UNIT_CODE: sanitizeString(
        process.env.REACT_APP_GPT_INTERSTITIAL_AD_UNIT_CODE
      ),
      ADSENSE_CLIENT: sanitizeString(process.env.REACT_APP_ADSENSE_CLIENT),
      ADSENSE_DISPLAY_SLOT: sanitizeString(
        process.env.REACT_APP_ADSENSE_DISPLAY_SLOT || process.env.REACT_APP_ADSENSE_HOMEPAGE_SLOT
      ),
    };
  }
  
  // Production environment: use window.APP_CONFIG (set by template substitution)
  // Fallback to REACT_APP_* if window.APP_CONFIG is not available
  return {
    API_BASE_URL: (window.APP_CONFIG && window.APP_CONFIG.API_BASE_URL) || process.env.REACT_APP_API_BASE_URL,
    GA_MEASUREMENT_ID: (window.APP_CONFIG && window.APP_CONFIG.GA_MEASUREMENT_ID) || process.env.REACT_APP_GA_MEASUREMENT_ID,
    POSTHOG_PROJECT_TOKEN: (window.APP_CONFIG && window.APP_CONFIG.POSTHOG_PROJECT_TOKEN) || process.env.REACT_APP_POSTHOG_PROJECT_TOKEN,
    POSTHOG_HOST: (window.APP_CONFIG && window.APP_CONFIG.POSTHOG_HOST) || process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com',
    POSTHOG_ENABLED: parseBoolean(
      (window.APP_CONFIG && window.APP_CONFIG.POSTHOG_ENABLED) || process.env.REACT_APP_POSTHOG_ENABLED,
      false
    ),
    POSTHOG_EVENT_DENYLIST: FRONTEND_POSTHOG_DENYLIST,
    GIT_SHA: (window.APP_CONFIG && window.APP_CONFIG.GIT_SHA) || process.env.REACT_APP_GIT_SHA,
    CLERK_PUBLISHABLE_KEY: (window.APP_CONFIG && window.APP_CONFIG.CLERK_PUBLISHABLE_KEY) || process.env.REACT_APP_CLERK_PUBLISHABLE_KEY,
    ENVIRONMENT: (window.APP_CONFIG && window.APP_CONFIG.ENVIRONMENT) || process.env.REACT_APP_ENVIRONMENT || 'production',
    SHOW_ADS: parseBoolean(
      (window.APP_CONFIG && window.APP_CONFIG.SHOW_ADS) || process.env.REACT_APP_SHOW_ADS,
      false
    ),
    AD_PROVIDER: parseAdProvider(
      (window.APP_CONFIG && window.APP_CONFIG.AD_PROVIDER) || process.env.REACT_APP_AD_PROVIDER,
      'H5'
    ),
    GPT_NETWORK_CODE: sanitizeString(
      (window.APP_CONFIG && window.APP_CONFIG.GPT_NETWORK_CODE)
      || process.env.REACT_APP_GPT_NETWORK_CODE
    ),
    GPT_INTERSTITIAL_AD_UNIT_CODE: sanitizeString(
      (window.APP_CONFIG && window.APP_CONFIG.GPT_INTERSTITIAL_AD_UNIT_CODE)
      || process.env.REACT_APP_GPT_INTERSTITIAL_AD_UNIT_CODE
    ),
    ADSENSE_CLIENT: sanitizeString(
      (window.APP_CONFIG && window.APP_CONFIG.ADSENSE_CLIENT) || process.env.REACT_APP_ADSENSE_CLIENT
    ),
    ADSENSE_DISPLAY_SLOT: sanitizeString(
      (window.APP_CONFIG && (window.APP_CONFIG.ADSENSE_DISPLAY_SLOT || window.APP_CONFIG.ADSENSE_HOMEPAGE_SLOT))
      || process.env.REACT_APP_ADSENSE_DISPLAY_SLOT
      || process.env.REACT_APP_ADSENSE_HOMEPAGE_SLOT
    ),
  };
};

export const config = getConfig(); 
